/**
 * 03 · Kapcsolat és számlázás. Handover §5.9 and §5.10.
 *
 * The *Még hiányzik:* list of §5.4 keeps its lead-in, unlike the order
 * blockers of §5.11 — DR-13 (`build/FIXES.md` item 16) took that lead-in off
 * the blockers because they are written in two moods, and left this one,
 * because every item in this list really is a missing thing.
 */

import type { PageBundle } from '../bundle.ts'
import { Choices, Note } from '../components.tsx'
import { postcodeOk } from '../format.ts'
import { missingItems, missingTexts, type MissingItem } from '../missing.ts'
import type { AddressFields, PageState } from '../state.ts'
import { pickedTown, townOffers, townsFor, typedTown, withPostcode } from '../towns.ts'

/**
 * §5.9 and §5.10: what has still to be given. On an enquiry only the four
 * ways of reaching the customer are needed — a colleague calls and the rest
 * is agreed then. Specification §3.1 (DECIDED 20261023) gives each part the
 * field it names (`missing.ts`); the sentences are unchanged.
 */
export function contactMissing(
  state: PageState,
  t: (hu: string, en: string) => string,
  enquiry: boolean,
): string[] {
  return missingTexts(missingItems(state, t, enquiry))
}

export function Step3Contact({ page }: { page: PageBundle }): JSX.Element {
  const { t, state, set, enquiry, locked } = page
  const missing = contactMissing(state, t, enquiry)
  const required = <span className="req">*</span>

  return (
    <section id="sec3">
      <div className="sechead">
        <span className="n">03</span>
        <h2>{t('Kapcsolat és számlázás', 'Contact and billing')}</h2>
      </div>
      <div className="card">
        <Choices
          id="bizOpt"
          dataAttribute="biz"
          choices={[
            { value: 'false', label: t('Magánszemélyként rendelek', 'Ordering as a private person') },
            { value: 'true', label: t('Cégként rendelek', 'Ordering as a business') },
          ]}
          value={String(state.business)}
          onChoose={(value) =>
            /* Run 16 item 4 (DECIDED 20261015, Barna): transfer is no longer
               a business method, so changing the buyer type no longer takes
               the payment choice away. The choice is the customer's, and it
               survives their saying who they are. */
            set({ business: value === 'true' })
          }
        />

        {/* §5.12: Hungarian puts the family name first and English does not,
            so the two fields change places with the language rather than
            changing their labels. */}
        <div className="grid2">
          <div id="wLast" style={{ order: t.lang === 'hu' ? 0 : 1 }}>
            <label className="f" htmlFor="cLast">
              {t('Vezetéknév', 'Last name')}
              {required}
            </label>
            <input
              type="text"
              id="cLast"
              autoComplete="family-name"
              disabled={locked}
              value={state.lastName}
              onChange={(event) => set({ lastName: event.target.value })}
            />
          </div>
          <div id="wFirst" style={{ order: t.lang === 'hu' ? 1 : 0 }}>
            <label className="f" htmlFor="cFirst">
              {t('Keresztnév', 'First name')}
              {required}
            </label>
            <input
              type="text"
              id="cFirst"
              autoComplete="given-name"
              disabled={locked}
              value={state.firstName}
              onChange={(event) => set({ firstName: event.target.value })}
            />
          </div>
        </div>

        <div className="grid2" style={{ marginTop: 12 }}>
          <div>
            <label className="f" htmlFor="cMail">
              {t('E-mail cím', 'Email address')}
              {required}
            </label>
            <input
              type="email"
              id="cMail"
              autoComplete="email"
              disabled={locked}
              value={state.email}
              onChange={(event) => set({ email: event.target.value })}
            />
          </div>
          <div>
            <label className="f" htmlFor="cTel">
              {t('Telefonszám', 'Phone number')}
              {required}
            </label>
            <input
              type="tel"
              id="cTel"
              autoComplete="tel"
              placeholder="+36 30 123 4567"
              disabled={locked}
              value={state.phone}
              onChange={(event) => set({ phone: event.target.value })}
            />
            <p className="sub" id="telHint" style={{ marginTop: 5 }}>
              {t(
                '+36… vagy nemzetközi formában. Kérdés esetén ezen hívjuk.',
                '+36… or an international number. We call this number if anything needs checking.',
              )}
            </p>
          </div>
        </div>

        {/* `hidden` is a UA `display:none`, and any author `display` beats it —
            `.grid2` is `display:grid`, so the HTML attribute hid nothing at all
            and a private person was asked for a company name. vB6 toggles its
            own `.hide`, which carries `!important`; so does this. */}
        <div className={`grid2${state.business ? '' : ' hide'}`} style={{ marginTop: 12 }} id="bizGrid">
          <div>
            <label className="f" htmlFor="cCo">
              {t('Cégnév', 'Company name')}
              {required}
            </label>
            <input
              type="text"
              id="cCo"
              autoComplete="organization"
              disabled={locked}
              value={state.company}
              onChange={(event) => set({ company: event.target.value })}
            />
          </div>
          <div>
            <label className="f" htmlFor="cVat">
              {t('Adószám', 'Tax number')}
              {required}
            </label>
            <input
              type="text"
              id="cVat"
              placeholder="12345678-2-41"
              disabled={locked}
              value={state.vatNumber}
              onChange={(event) => set({ vatNumber: event.target.value })}
            />
          </div>
        </div>

        <h3 className="addrhd" id="hBill">
          {t('Számlázási cím', 'Billing address')}
        </h3>
        <Address page={page} which="billing" />

        <div id="shipWrap" className={state.fulfilment === 'delivery' ? undefined : 'hide'}>
          <label className="chk" style={{ marginTop: 14 }}>
            <input
              type="checkbox"
              id="sameAddr"
              checked={state.sameAddress}
              disabled={locked}
              onChange={(event) => {
                /* Fix list item 1 (sweep observation 6): unticking opens the
                   delivery address on the step 01 postcode, the one the
                   delivery was priced for, instead of empty. */
                const same = event.target.checked
                set((current) =>
                  same || current.shipping.postcode !== '' || !/^\d{4}$/.test(current.postcode)
                    ? { sameAddress: same }
                    : {
                        sameAddress: same,
                        /* §3.7: the town comes from the postcode lookup. */
                        shipping: withPostcode(current.shipping, current.postcode, page.towns),
                      },
                )
              }}
            />
            <span id="sameTxt">
              {t(
                'A szállítási cím megegyezik a számlázási címmel',
                'The delivery address is the same as the billing address',
              )}
            </span>
          </label>
          <div id="shipFields" className={state.sameAddress ? 'hide' : undefined}>
            <h3 className="addrhd" id="hShip">
              {t('Szállítási cím', 'Delivery address')}
            </h3>
            <Address page={page} which="shipping" />
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <label className="f" htmlFor="cNote">
            {t('Megjegyzés a szállításhoz', 'Note for the delivery')}
          </label>
          <input
            type="text"
            id="cNote"
            maxLength={300}
            disabled={locked}
            placeholder={t(
              'pl. a portán Kovács Éva veszi át',
              'e.g. Éva Kovács takes delivery at reception',
            )}
            value={state.deliveryNote}
            onChange={(event) => set({ deliveryNote: event.target.value })}
          />
        </div>

        <div id="contactNote">
          {missing.length > 0 ? (
            <Note>
              {t('Még hiányzik', 'Still missing')}: <MissingLinks page={page} items={page.missing} />.
              {/* Specification §3.1 (DECIDED 20261023): the notice takes the
                  customer to the first gap on the page. */}
              <div className="missingjump">
                <button type="button" className="linkish" id="missingFirst" onClick={page.goToFirstMissing}>
                  {t('Ugrás az első hiányzó mezőhöz', 'Go to the first missing field')}
                </button>
              </div>
            </Note>
          ) : (
            <Note tone="ok">
              {t('Minden kötelező adat megvan.', 'Every required detail is in place.')}
            </Note>
          )}
        </div>
      </div>
    </section>
  )
}

/**
 * Specification §3.1 (DECIDED 20261023): each item of the notice links to its
 * field, in the notice's own words and punctuation. An address item keeps its
 * lead-in as text and links each missing part.
 */
function MissingLinks(props: { page: PageBundle; items: MissingItem[] }): JSX.Element {
  const { page, items } = props
  const link = (text: string, field: string): JSX.Element => (
    <a
      href={`#${field}`}
      data-missing={field}
      onClick={(event) => {
        event.preventDefault()
        page.goToField(field)
      }}
    >
      {text}
    </a>
  )
  return (
    <>
      {items.map((item, index) => (
        <span key={index}>
          {index > 0 ? ', ' : ''}
          {item.lead === null ? null : `${item.lead}: `}
          {item.parts.map((part, partIndex) => (
            <span key={part.field}>
              {partIndex > 0 ? ', ' : ''}
              {link(part.text, part.field)}
            </span>
          ))}
        </span>
      ))}
    </>
  )
}

/** §5.10: postcode, town, street and number, floor and door (optional). */
function Address(props: { page: PageBundle; which: 'billing' | 'shipping' }): JSX.Element {
  const { page, which } = props
  const { t, state, set, locked } = page
  const prefix = which === 'billing' ? 'b' : 's'
  const address = state[which]
  const autoComplete = which === 'billing' ? 'billing' : 'shipping'
  const required = <span className="req">*</span>
  const lookedUp = townsFor(page.towns, address.postcode) ?? []
  const offers = townOffers(address, page.towns)

  const update = (patch: Partial<AddressFields>): void =>
    set((current) => {
      /* Specification §3.7: a new postcode looks the town up (`towns.ts`),
         and leaves a town the customer typed alone. */
      const next =
        patch.postcode === undefined
          ? { ...current[which], ...patch }
          : withPostcode({ ...current[which], ...patch }, patch.postcode, page.towns)
      /* The delivery postcode and the step 01 postcode are one value: editing
         either updates the other. DR-22 (FIXES item 25) took away the guards
         that used to stop this under pickup, so the two never diverge. */
      const isShipping = which === (current.sameAddress ? 'billing' : 'shipping')
      const carry =
        patch.postcode !== undefined && isShipping && postcodeOk(patch.postcode)
          ? { postcode: patch.postcode }
          : {}
      return { [which]: next, ...carry }
    })

  return (
    <div className="addr" data-addr={prefix}>
      <div>
        <label className="f" htmlFor={`${prefix}Zip`} id={`lab${prefix}Zip`}>
          {t('Irányítószám', 'Postcode')}
          {required}
        </label>
        <input
          type="text"
          id={`${prefix}Zip`}
          maxLength={4}
          inputMode="numeric"
          autoComplete={`${autoComplete} postal-code`}
          disabled={locked}
          value={address.postcode}
          onChange={(event) => update({ postcode: event.target.value })}
        />
      </div>
      <div>
        <label className="f" htmlFor={`${prefix}City`} id={`lab${prefix}City`}>
          {t('Település', 'Town')}
          {required}
        </label>
        <input
          type="text"
          id={`${prefix}City`}
          autoComplete={`${autoComplete} address-level2`}
          disabled={locked}
          value={address.town}
          list={lookedUp.length > 0 ? `${prefix}CityList` : undefined}
          onChange={(event) => {
            const town = event.target.value
            set((current) => ({ [which]: typedTown(current[which], town) }))
          }}
        />
        {lookedUp.length > 0 ? (
          <datalist id={`${prefix}CityList`}>
            {lookedUp.map((town) => (
              <option key={town} value={town} />
            ))}
          </datalist>
        ) : null}
        {/* §3.7: several settlements, or a typed town beside the looked-up
            one. The list never replaces what is in the field by itself. */}
        {offers.length > 0 && !locked ? (
          <div
            className="opts townopts"
            id={`${prefix}CityOffers`}
            role="group"
            aria-labelledby={`lab${prefix}City`}
          >
            {offers.map((town) => (
              <button
                key={town}
                type="button"
                data-town={town}
                onClick={() => set((current) => ({ [which]: pickedTown(current[which], town) }))}
              >
                {town}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      <div className="full">
        <label className="f" htmlFor={`${prefix}Street`} id={`lab${prefix}Street`}>
          {t('Utca, házszám', 'Street and number')}
          {required}
        </label>
        <input
          type="text"
          id={`${prefix}Street`}
          autoComplete={`${autoComplete} address-line1`}
          disabled={locked}
          placeholder={t('pl. Fő utca 1.', 'e.g. Fő utca 1')}
          value={address.street}
          onChange={(event) => update({ street: event.target.value })}
        />
      </div>
      <div className="half">
        <label className="f" htmlFor={`${prefix}Floor`} id={`lab${prefix}Floor`}>
          {t('Emelet, ajtó (nem kötelező)', 'Floor, door (optional)')}
        </label>
        <input
          type="text"
          id={`${prefix}Floor`}
          autoComplete={`${autoComplete} address-line2`}
          disabled={locked}
          value={address.floor}
          onChange={(event) => update({ floor: event.target.value })}
        />
      </div>
    </div>
  )
}
